import React, { Suspense } from "react";

import cx from "classnames";

import { ErrorBoundary } from "~/integrations/actionItemIntegration/ErrorBoundary";
import type { ActionItemIntegrationWithPanes } from "~/integrations/actionItemIntegration/index";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import { __globalModalContext } from "~/ui/modals";

import styles from "../jira/css/IntegrationLink.module.css";

type ModalState = "create" | "import";

export interface ActionItemLinkingModalProps {
	initialText: string;

	integration: ActionItemIntegrationWithPanes;

	userId: string;
	blockKey: string;
	noteId: string;
}

function ActionItemLinkingModal({
	integration,
	initialText,
	close,
	blockKey,
	noteId,
}: ActionItemLinkingModalProps & { close: () => void }) {
	const [selectedTab, setSelectedTab] = React.useState<ModalState>(initialText.length > 0 ? "create" : "import");
	const [canSubmit, setCanSubmit] = React.useState<boolean>(false);
	const [submit, _rawSetSubmit] = React.useState<() => Promise<boolean>>();

	const setSubmit = React.useCallback(
		(setSubmit: () => Promise<boolean>) => {
			_rawSetSubmit(() => setSubmit);
		},
		[_rawSetSubmit],
	);

	const importOrCreate = React.useCallback(() => {
		if (submit) {
			submit().then(close);
		}
	}, [submit, close]);

	function onTabClicked(newTab: ModalState) {
		return (e: React.MouseEvent) => {
			e.preventDefault();
			setSelectedTab(newTab);
		};
	}

	const onImportTabClicked = React.useMemo(() => onTabClicked("import"), []);
	const onCreateNewTabClicked = React.useMemo(() => onTabClicked("create"), []);

	const headerWithoutNav = (
		<>
			<Modal.Title>
				{integration.logo} {integration.displayName}
			</Modal.Title>
			<Modal.HeaderActions onClose={close} />
		</>
	);

	return (
		<Modal onClose={close}>
			<ErrorBoundary headerWithoutNav={headerWithoutNav}>
				<Modal.Header>
					{headerWithoutNav}
					<nav className={cx(styles.tabBar)}>
						<a
							key="create"
							href="#"
							className={cx(styles.tab, { [styles.tabActive]: selectedTab === "create" })}
							onClick={onCreateNewTabClicked}
						>
							Create new {integration.externalActionItemName}
						</a>
						<a
							key="link"
							href="#"
							className={cx(styles.tab, { [styles.tabActive]: selectedTab === "import" })}
							onClick={onImportTabClicked}
						>
							Import existing {integration.externalActionItemName}
						</a>
					</nav>
				</Modal.Header>
				<Modal.Body className={styles.body}>
					<Suspense
						fallback={
							<Modal.Body style={{ margin: "1.5em" }}>
								<LoadingSpinner dimmer={false} />
							</Modal.Body>
						}
					>
						{selectedTab === "create" && (
							<integration.renderCreateNewExternalTicketPane
								initialText={initialText}
								registerSubmit={setSubmit}
								setCanSubmit={setCanSubmit}
								blockKey={blockKey}
								noteId={noteId}
							/>
						)}
						{selectedTab === "import" && (
							<integration.renderImportExistingExternalTicketPane
								initialText={initialText}
								registerSubmit={setSubmit}
								setCanSubmit={setCanSubmit}
								blockKey={blockKey}
								noteId={noteId}
							/>
						)}
					</Suspense>
				</Modal.Body>
				<Modal.Footer>
					<Button size="normal" variant="secondary" onClick={close}>
						Cancel
					</Button>
					{selectedTab == "create" && (
						<Button size="normal" variant="primary" onClick={importOrCreate} disabled={!canSubmit}>
							Create {integration.externalActionItemName}
						</Button>
					)}
					{selectedTab === "import" && (
						<Button size="normal" variant="primary" onClick={importOrCreate} disabled={!canSubmit}>
							Import {integration.externalActionItemName}
						</Button>
					)}
				</Modal.Footer>
			</ErrorBoundary>
		</Modal>
	);
}

export type CreateOrImportProps = {
	registerSubmit: (submit: () => Promise<boolean>) => void;
	setCanSubmit: (canSubmit: boolean) => void;
	initialText: string;
	blockKey: string;
	noteId: string;
};

export type RenderCreateNewExternalTicketPaneType = (props: CreateOrImportProps) => React.ReactElement;
export type RenderImportExistingExternalTicketPane = (props: CreateOrImportProps) => React.ReactElement;

export async function displayActionItemLinkingModal(props: ActionItemLinkingModalProps) {
	return await __globalModalContext?.open(({ close }) => <ActionItemLinkingModal {...props} close={close} />);
}
